import { useState } from 'react'
import { Collapse, Drawer, IconButton } from '@mui/material'
import { CaretDownIcon, ClockCounterClockwiseIcon, XIcon } from '@phosphor-icons/react'
import { format, parseISO } from 'date-fns'
import { useQuery } from '@tanstack/react-query'

import { Breadcrumb } from '@components/shared/Breadcrumb'
import { Button } from '@components/shared/Button'
import { currencyMask } from '@utils/masks/currencyMask'
import { emotionTheme } from '@styles/theme'
import { getAccountIcon } from '@pages/ConsolidatedEntry/components/ConsolidatedGrid/helpers/getAccountIcon'
import { getHistoryStatus } from '@pages/PreviewManagement/components/PreviewHistoryModal/helpers/parsePreviewHistoryGrid'
import { getPreviewStatusConfig } from '@components/shared/BudgetPreviewsStatus'
import { Tooltip } from '@components/shared/Tooltip'
import { useUser } from '@hooks/useUser'

import type { ColumnDefProps } from '@components/shared/Table/types'

import * as apiPreviewManagement from '@services/api/previewManagement'
import * as S from './styles'

interface PreviewAccountProps {
  id: number
  name: string
}

// Mesmo enumerador de contas das telas de Prévia e Gestão da Prévia
export const PREVIEW_ACCOUNTS = {
  invoicing: { id: 1, name: 'Faturamento' },
  consumption: { id: 2, name: 'Consumo' },
  people: { id: 3, name: 'Pessoal' },
  otherExpenses: { id: 4, name: 'Outras Despesas' },
  depreciation: { id: 5, name: 'Depreciação' },
  bonus: { id: 6, name: 'Bonificação' }
} satisfies Record<string, PreviewAccountProps>

// Logs de histórico só estão completos em produção a partir de P03-Nov/26
const HISTORY_START_PERIOD = '2026-11'

const HistoryButton = ({ onClick }: { onClick: () => void }) => {
  const { filiData } = useUser()
  // Todas as linhas da tabela são semanas da competência selecionada no header
  const isAvailable = (filiData?.startPeriod?.slice(0, 7) ?? '') >= HISTORY_START_PERIOD

  return (
    <Tooltip title={isAvailable ? 'Ver histórico' : 'Disponível a partir de P03-Nov/26'} placement="top">
      <IconButton
        size="small"
        disabled={!isAvailable}
        onClick={onClick}
        sx={{
          borderRadius: '8px',
          color: emotionTheme.colors.primary,
          background: emotionTheme.colors.gray.light,
          '&.Mui-disabled': { opacity: 0.4, color: emotionTheme.colors.primary }
        }}
      >
        <ClockCounterClockwiseIcon size={20} weight="bold" />
      </IconButton>
    </Tooltip>
  )
}

// Coluna "Ações" das tabelas de prévias semanais
export const previewHistoryColumn = (onClick: () => void): ColumnDefProps => ({
  id: 'actions',
  columnName: 'Ações',
  align: 'center',
  minWidth: '5rem',
  maxWidth: '5rem',
  stickyPosition: 'right',
  withoutTooltip: true,
  value: <HistoryButton onClick={onClick} />
})

interface AccountTimelineProps {
  account: PreviewAccountProps
  weekNumber: number
  defaultOpen: boolean
}

const AccountTimeline = ({ account, weekNumber, defaultOpen }: AccountTimelineProps) => {
  const { filiData } = useUser()
  const [open, setOpen] = useState(defaultOpen)

  const { data, isLoading } = useQuery({
    queryKey: ['preview-account-status-history', filiData?.filiCode, filiData?.startPeriod, account.id, weekNumber],
    queryFn: () =>
      apiPreviewManagement.searchPreviewStatusHistory({
        referenceDate: filiData!.startPeriod,
        branchCodeFilter: [filiData!.filiCode],
        accountId: account.id,
        weekNumber,
        sortBy: 'createdAt',
        sortType: 'DESC',
        currentPage: 1,
        // ponytail: sem paginação — uma semana de uma conta raramente passa disso
        itemsPerPage: 100
      }),
    // Só busca quando a seção é aberta; 'ALL' (visão de todas as filiais) não tem histórico de uma filial só
    enabled: open && !!filiData?.filiCode && filiData.filiCode !== 'ALL'
  })

  const items = data?.items ?? []
  // Ícone e cor padrão da conta (mesmos da tabela da Gestão da Prévia)
  const accountIcon = getAccountIcon(account.name)

  return (
    <S.AccountSection>
      <S.AccountTitle type="button" aria-expanded={open} onClick={() => setOpen(!open)}>
        <S.AccountIcon style={{ color: accountIcon.color }}>{accountIcon.icon}</S.AccountIcon>
        {account.name}
        <CaretDownIcon size={18} weight="bold" style={{ transform: open ? 'rotate(180deg)' : undefined }} />
      </S.AccountTitle>

      <Collapse in={open} timeout="auto">
        <S.AccountBody>
          {isLoading && <S.EmptyText>Carregando...</S.EmptyText>}
          {!isLoading && !items.length && (
            <S.EmptyText>Nenhuma atualização encontrada para esta semana.</S.EmptyText>
          )}

          <S.Timeline>
            {items.map((item, index) => {
              const status = getHistoryStatus(item)
              const config = status ? getPreviewStatusConfig(status) : undefined

              return (
                <S.TimelineItem key={item.id}>
                  <S.StatusIcon
                    style={{
                      color: config?.color ?? emotionTheme.colors.secondary,
                      background: config?.background ?? emotionTheme.colors.gray[300]
                    }}
                  >
                    {config?.icon}
                  </S.StatusIcon>

                  <S.ItemBody>
                    <S.ItemHeader>
                      <S.StatusLabel>{config?.label ?? status?.descriptionPtbr ?? '–'}</S.StatusLabel>
                      {index === 0 && <S.LatestTag>Mais recente</S.LatestTag>}
                    </S.ItemHeader>

                    <S.ItemDate>{format(parseISO(item.createdAt), "dd/MM/yyyy 'às' HH:mm:ss")}</S.ItemDate>

                    <S.ItemDetails>
                      <div>
                        <S.ItemText>Valor da Prévia</S.ItemText>
                        <S.ItemText>
                          {item.responsibleUser?.name ?? item.userProfile?.name ?? '–'}
                          {!!item.responsibleUser && !!item.userProfile?.name && ` · ${item.userProfile.name}`}
                        </S.ItemText>
                      </div>
                      <S.Amount>{item.previewAmount != null ? currencyMask(item.previewAmount) : '–'}</S.Amount>
                    </S.ItemDetails>
                  </S.ItemBody>
                </S.TimelineItem>
              )
            })}
          </S.Timeline>
        </S.AccountBody>
      </Collapse>
    </S.AccountSection>
  )
}

interface PreviewHistoryDrawerProps {
  /** Rótulo da tela no breadcrumb (ex.: "Faturamento") */
  pageLabel: string
  accounts: PreviewAccountProps[]
  /** Semana aberta; null fecha o drawer */
  weekNumber: number | null
  onClose: () => void
}

export const PreviewHistoryDrawer = ({ pageLabel, accounts, weekNumber, onClose }: PreviewHistoryDrawerProps) => {
  const { filiData, userData } = useUser()

  const startPeriod = filiData?.startPeriod
  // Mesmo label do select "Período Competência" do header (ex.: "P01 - Setembro") + ano
  const competenceLabel = startPeriod
    ? userData?.competencePeriod?.find((item) => item.monthNumber === Number(startPeriod.slice(5, 7)))?.label
    : undefined

  return (
    <Drawer anchor="right" open={!!weekNumber} onClose={onClose} PaperProps={{ component: S.Paper }}>
      <S.Header>
        <S.BreadcrumbBox>
          <Breadcrumb
            breadcrumbData={[
              { label: 'Gestão Financeira' },
              { label: `${pageLabel} / ${filiData?.filiCode} - ${filiData?.filiName}` }
            ]}
          />
        </S.BreadcrumbBox>
        <S.CloseButton onClick={onClose}>
          <XIcon size={22} weight="bold" color={emotionTheme.colors.secondary} />
        </S.CloseButton>
      </S.Header>

      <S.Content>
        <S.TitleRow>
          <S.Title>Histórico da Prévia</S.Title>
          {!!competenceLabel && (
            <S.CompetenceBadge>
              Competência: {competenceLabel}/{startPeriod!.slice(2, 4)}
            </S.CompetenceBadge>
          )}
          <S.WeekBadge>Semana {weekNumber}</S.WeekBadge>
        </S.TitleRow>

        {!!weekNumber &&
          // Só a conta principal da tela abre expandida; as demais (ex.: Bonificação no Consumo) ficam recolhidas
          accounts.map((account, index) => (
            <AccountTimeline key={account.id} account={account} weekNumber={weekNumber} defaultOpen={index === 0} />
          ))}
      </S.Content>

      <S.Footer>
        <Button width={137} colorType="outlineBlue" onClick={onClose}>
          Voltar
        </Button>
      </S.Footer>
    </Drawer>
  )
}
